import { useEffect, useRef, useState } from "react";

import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridNoRecords,
} from "@progress/kendo-react-grid";

import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { DeleteModelDialog, DeletePostDataInfo, DeleteStatus } from "../../../core/delete-model-dialog.component";
import SubGroupModel from "../../../models/products/subgroup-model";
import { useGetSubGroups } from "../../../apiclient/product/useGetSubGroupApi";
import { SubmitStatus } from "../../../core/formik-model-dialog.component";
import { API_END_POINT } from "../../../constants";
import { GridCommandCell } from "../../../core/grid-command-cell.component";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import SubGroupEntry from "../../subgroup/subgroup-entry";
import { useNavigate } from "react-router-dom";
import { useGetProductInvoiceList } from "../../../apiclient/transaction/useGetProductInvoiceApi";
import ProductInvoiceHistoryModel from "../../../models/transaction/productinvoicehistory-model";
import ProductInvoiceEntryPageNew from "./productinvoicenew-entry";
import EditIcon, { PrintIcon } from "../../../assets/images/edit";
import DeleteIcon from "../../../assets/images/delete";
import { getDataWithToken, getEndPoint } from "../../../Utils/network.client";
import ProductInvoiceEntryPage from "./productinvoice-entry";
import ProductInvoicePage from "./productinvoice.page";

interface ProductInvoiceHistoryPageProps {
  onEdit?: (data: ProductInvoiceHistoryModel) => void;  // Make onEdit optional
  setActivePage: (page: "list" | "entry") => void; // Add this prop to handle page changes
}

const html2pdf = require("html2pdf.js");

export const addOrUpdateArrayItem = (data: any[], item: any, key: string) => {
  const newData = data ? [...data] : [];
  const index = data?.findIndex((d) => d[key] === item[key]);
  console.log("the index is" + index);
  if (index > -1) {
    newData.splice(index, 1, item);
  } else {
    newData.push(item);
  }
  return newData;
};

const ProductInvoiceHistoryPage: React.FC<ProductInvoiceHistoryPageProps> = ({ onEdit, setActivePage }) => {
  const initialDataState: State = {
    take: 10,
    skip: 0,
    group: [],
  };
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [deleteProductInvoiceData, setDeleteProductInvoiceData] = useState<DeletePostDataInfo>();
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const [resultData, setResultData] = useState<DataResult>();
  const [editModal, setEditModal] = useState<boolean>(false);
  const exportExcel = useRef<ExcelExport | null>(null);
  const [deleted, isDeleted] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ProductInvoiceHistoryModel | null>(null);
  const [productInvoiceHtml, setProductInvoiceHtml] = useState<string>("");
  const navigate = useNavigate();
  const [editData, setEditData] = useState<ProductInvoiceHistoryModel | null>(null);
  const [showListPage, setShowListPage] = useState(false);
  const [activePage, setActiveLocalPage] = useState<"list" | "entry">("list");

  const {
    data: result,
    isLoading,
    isError,
    refetch: realodProductInvoice,
  } = useGetProductInvoiceList();

  console.log("res", result);

  const processWithGroups = (data: ProductInvoiceHistoryModel[], dataState: State) => {
    const groups = dataState.group;
    dataState.group = groups;
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  useEffect(() => {
    setEditData(null);
    if (result) {
      setResultData(processWithGroups(result, initialDataState));
    }
  }, [result]);

  let newData: any = null;
  if (resultData) {
    newData = setExpandedState({
      data: resultData.data,
      collapsedIds: collapsedState,
    });
  }

  const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} />
      </div>
    );
  };

  const expandChange = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;

    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(result, event.dataState);
    setResultData(newDataState);
    setDataState(event.dataState);
  };

  const handleProceedInvoice = async (dataItem: any) => {
    const getPrintProductInvoiceUrl = getEndPoint(API_END_POINT.ORDER) + `/api/ProductInvoice/GetProductInvoiceforPdf/${dataItem.productInvoiceMainId}/${dataItem.orderDate}`;
    try {
      const response = await getDataWithToken<any>(getPrintProductInvoiceUrl);
      const printProductInvoice = response?.data?.result;
      if (printProductInvoice) {
        setProductInvoiceHtml(printProductInvoice);
        console.log(productInvoiceHtml);
        await convertHtmlToPdf(printProductInvoice);
      } else {
        console.error("No invoice data found.");
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const convertHtmlToPdf = async (productInvoiceHtml: string) => {
    const element = document.createElement('div');
    element.innerHTML = productInvoiceHtml;
    document.body.appendChild(element);
    const options = {
      filename: 'invoice.pdf',
      html2canvas: {
        dpi: 500,
        scale: 5,
        letterRendering: true,
        useCORS: true,
        allowTaint: false,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      }
    };
    html2pdf()
      .set(options)
      .from(element)
      .save('invoice.pdf')
      .then(() => {
        document.body.removeChild(element);
      })
      .catch((error: any) => {
        console.error('Error while generating PDF:', error);
      });
  };

  const handleEditProductInvoice = (dataItem: any) => {
    const ordDate = new Date(dataItem.orderDate);
    const updatedDataItem = { ...dataItem, orderDate2: ordDate };
    setEditData(updatedDataItem);
    console.log("editData",updatedDataItem)
    setActiveLocalPage("entry"); // switch to entry page on edit
  };

  const handleCancelEntry = () => {
    setEditData(null);
    setActiveLocalPage("list"); // return to list page
  };

  const confirmDelete = async (dataItem: any) => {
    const confirmed = window.confirm("Do you want to delete this invoice?");
    if (confirmed) {
      const getDeleteInvoiceUrl = getEndPoint(API_END_POINT.ORDER) + `/api/ProductInvoice/DeleteProductInvoice/${dataItem.productInvoiceMainId}`;
      try {
        const response = await getDataWithToken<any>(getDeleteInvoiceUrl);
        const res = response?.data?.result;
        if (res) {
          realodProductInvoice();
        } else {
          console.error("No invoice data found.");
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
    }
    setSelectedInvoice(null);
  };

  let grid = null;
  if (result) {
    grid = (
      <Grid
        data={newData}
        pageable={{ pageSizes: [10, 20, 50, 100] }}
        total={resultData?.total}
        groupable={true}
        sortable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
        onExpandChange={expandChange}
        expandField="expanded"
        columnVirtualization={false}
        resizable={true}
        style={{ width: '100%' }}
      >
        <GridColumn field="orderDate" title="Order Date" columnMenu={ColumnMenu} />
        <GridColumn field="deliveryDate" title="Delivery Date" columnMenu={ColumnMenu} />
        <GridColumn field="invoiceNumber" title="Invoice No" />
        <GridColumn field="droppingPointCode" title="Code" columnMenu={ColumnMenu} />
        <GridColumn field="droppingPointName" title="Name" columnMenu={ColumnMenu} />
        <GridColumn field="netAmount" title="Amount" />
        <GridColumn
          field="edit"
          width={"180px"}
          title="Actions"
          cell={(props) => (
            <td>
              <button
                className="action-btns k-grid-edit-command"
                onClick={() => handleEditProductInvoice(props.dataItem)}
              >
                <EditIcon />{" "}
              </button>
              <button
                className="action-btns k-grid-remove-command"
                onClick={() => confirmDelete(props.dataItem)}
              >
                <DeleteIcon />{" "}
              </button>
              <button
                className="action-btns k-grid-print-command"
                onClick={() => handleProceedInvoice(props.dataItem)}
              >
                <PrintIcon />{" "}
              </button>
            </td>
          )}
        />
        <GridNoRecords>
          <div className="mt-2" style={{ width: "100%", height: "500px" }}>
            <center>
              <h5>No data found</h5>
            </center>
          </div>
        </GridNoRecords>
      </Grid>
    );
  }

  if (isError) {
    return (
      <div>
        <h4>Error Loading Page..</h4>
      </div>
    );
  }

  return (
    <>
      {activePage === "entry" && editData ? (
        <ProductInvoiceEntryPage
          data={editData}
          onClose={handleCancelEntry}  // closes entry and returns to list
          setActivePage={setActiveLocalPage}
        />
      ) : (
        <div style={{ minHeight: "500px" }}>
          <div className="top-sec-align row">
            <div className="d-flex" style={{ width: "100%", justifyContent: "space-between", alignItems: "center" }}>
              <h5 className="text-normal title-web mb-0">Product Invoice History</h5>
              {/* <Button
              className="btn btn-secondary"
              onClick={() => setActivePage("list")} // Changed from "agentliterorderpage" to "list"
            >
              ← Back
            </Button> */}
            </div>
          </div>

          <LoadingIndicator isLoading={isLoading} />

          {selectedInvoice && (
            <DeleteModelDialog
              title="Delete Invoice"
              onClose={(status: DeleteStatus) => {
                if (status === DeleteStatus.Completed) {
                  // Perform any delete actions here
                } else {
                  setSelectedInvoice(null);
                }
              }}
            >
              <div>
                Are you sure you want to delete Invoice :{" "}
                <span style={{ fontWeight: "bold" }}>
                  {selectedInvoice.invoiceNumber}
                </span>
                ?
              </div>
            </DeleteModelDialog>
          )}

          <div>{grid}</div>
          <div className="mt-3" style={{ marginLeft: "20px" }}>
            <button
              className="btn btn-primary btn-sm"
              style={{ height: '36px', marginTop: '20px' }}
              onClick={() => setActivePage("list")}  // Changed from "agentliterorderpage" to "list"
            >
              ← Back
            </button>
          </div>
        </div>
      )}
    </>
  );
};
export default ProductInvoiceHistoryPage;

